import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, ResizeNotifier, setHeaderMenuStyle, setSubmenuClipHeight } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeNotifier | null}
   */
  #submenuResizeObserver = null;

  /**
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #deactivateTimer = null;
 
  /**
   * How long (ms) to wait before closing after the cursor leaves —
   * gives the user time to move from the trigger into the mega-menu.
   */
  static DEACTIVATE_DELAY = 1000;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#cleanupSubmenuResizeObserver();
    this.#clearDeactivateTimer();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Cancels any pending close so re-entering a trigger is instant.
   */
  #clearDeactivateTimer() {
    if (this.#deactivateTimer !== null) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = null;
    }
  }

  /**
   * Closes the menu after a short delay, giving the cursor time to reach the mega-menu.
   */
  #scheduleDeactivate() {
    this.#clearDeactivateTimer();
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = null;
      this.#deactivate();
    }, HeaderMenu.DEACTIVATE_DELAY);
  }

  #overflowSubmenuListener = () => {
    // Use the same delayed deactivation so moving back into the trigger
    // or another menu item doesn't flicker.
    this.#scheduleDeactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /** @type {HTMLElement | null} */
  #activeSubmenu = null;

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Cancel any in-flight close timer — the user moved back into a trigger.
    this.#clearDeactivateTimer();

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      this.#activeSubmenu = submenu;
      this.#observeSubmenuHeightChanges(submenu, isDefaultSlot, hasSubmenu);
    } else {
      this.#activeSubmenu = null;
    }

    const finalHeight = submenu ? this.#getSubmenuHeight(submenu, isDefaultSlot, hasSubmenu) : 0;

    this.#applySubmenuHeight(finalHeight);
    this.#scheduleSubmenuHeightUpdate(submenu, isDefaultSlot, hasSubmenu);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;
 
    const menu = findSubmenu(this.#state.activeItem);
 
    // Cursor is moving into the open submenu — don't close.
    const isMovingToSubmenu = event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
 
    // Focus is moving within the menu (keyboard navigation).
    const isMovingWithinMenu = event.type === 'blur' && event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
 
    // Cursor is moving to an overflow slot item.
    const isMovingToOverflowMenu = event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');
 
    if (isMovingToSubmenu || isMovingWithinMenu || isMovingToOverflowMenu) return;
 
    this.#scheduleDeactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    if (this.headerComponent) {
      setSubmenuClipHeight(this.headerComponent, 0);
    }
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.#activeSubmenu = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }

    this.#cleanupMutationObserver();
    this.#cleanupSubmenuResizeObserver();
  };


  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * @param {number} submenuHeight
   */
  #applySubmenuHeight(submenuHeight) {
    if (!this.headerComponent) return;

    setSubmenuClipHeight(this.headerComponent, submenuHeight);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  #cleanupSubmenuResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }

  /**
   * @param {HTMLElement} submenu
   * @returns {HTMLElement}
   */
  #getSubmenuMeasurementTarget(submenu) {
    const inner = submenu.querySelector('.menu-list__submenu-inner');
    return inner instanceof HTMLElement ? inner : submenu;
  }

  /**
   * @param {HTMLElement} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @returns {number}
   */
  #getSubmenuHeight(submenu, isDefaultSlot, hasSubmenu) {
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        return Math.max(overflowHeight, overflowListHeight);
      }

      return overflowListHeight;
    }

    return this.#getSubmenuMeasurementTarget(submenu).offsetHeight;
  }

  /**
   * Re-measure after layout settles (content-visibility, fonts, grid wrap).
   * @param {HTMLElement | null | undefined} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   */
  #scheduleSubmenuHeightUpdate(submenu, isDefaultSlot, hasSubmenu) {
    if (!submenu || !this.headerComponent) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.#activeSubmenu !== submenu) return;

        const height = this.#getSubmenuHeight(submenu, isDefaultSlot, hasSubmenu);
        if (height > 0) {
          this.#applySubmenuHeight(height);
        }
      });
    });
  }

  /**
   * @param {HTMLElement} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   */
  #observeSubmenuHeightChanges(submenu, isDefaultSlot, hasSubmenu) {
    this.#cleanupMutationObserver();
    this.#cleanupSubmenuResizeObserver();

    const updateHeight = () => {
      if (this.#activeSubmenu !== submenu) return;

      const height = this.#getSubmenuHeight(submenu, isDefaultSlot, hasSubmenu);
      if (height > 0) {
        this.#applySubmenuHeight(height);
      }
    };

    const scheduleUpdate = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(updateHeight);
      });
    };

    // Monitor DOM mutations to catch deferred content injection (from section hydration)
    this.#submenuMutationObserver = new MutationObserver(scheduleUpdate);
    this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

    const measurementTarget = this.#getSubmenuMeasurementTarget(submenu);
    this.#submenuResizeObserver = new ResizeNotifier(() => {
      scheduleUpdate();
    });
    this.#submenuResizeObserver.observe(measurementTarget);

    // Final re-measure before disconnecting observers
    setTimeout(() => {
      if (this.#activeSubmenu === submenu) {
        updateHeight();
      }
      this.#cleanupMutationObserver();
      this.#cleanupSubmenuResizeObserver();
    }, 500);
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
